/* eslint-disable react-hooks/exhaustive-deps */
import axios from 'axios';
import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { FaPlus, FaSearchMinus, FaSearchPlus } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { useWindowSize } from '../../hooks/useWindowSize';
import { addCondition, addNum, addText } from '../../services/item';
import {
  setCurrentMobileTab,
  setOpenModales,
  setPicZoom,
} from '../../store/global';
import { saveAll } from '../../store/items';
import {
  getId,
  canEditItemForItem,
  isBuiltinTabId,
  isTabCampaignAccessible,
} from '../../utils/item';
import { isEditable, isReadOnly } from '../../utils/roles';
import { canUseMjForItem, isMjForItem } from '../../utils/auth';
import { BoardGrid } from '../board/board.Grid';
import { BoardMap } from '../board/map/board.Map.js';
import { BoardToolBoxMap } from '../board/map/board.ToolBoxMap.js';
import { useBoardMapController } from '../../hooks/useBoardMapController';
import { BtnRound } from '../form/btn.Round';
import { LinkAll } from '../link/link.All';
import { NavigationGlobal } from '../navigation/navigation.Global';
import { PicAll } from '../pic/pic.All';
import { TabAll } from '../tab/tab.All';
import { TextAll } from '../text/text.All';
import { NumAll } from '../num/num.All';
import { ConditionAll } from '../condition/condition.All';
import { ItemToolBox } from './item.ToolBox';
import { RiLayoutMasonryFill, RiLayoutGridFill } from 'react-icons/ri';

export const ItemLarge = props => {
  const {
    itemId,
    disableSelfFetch = false,
    containerWidth,
    hideNavigation,
    ignoreSidebar,
    useMobileTabState,
    sidebarOpenOverride,
    onSidebarOpen,
  } = props;

  const item = useSelector(state => state.items[itemId]);
  const items = useSelector(state => state.items);
  const sidebar = useSelector(state => state.global.sidebar);
  const navigation = useSelector(state => state.global.navigation);
  const currentTab = useSelector(state => state.global.currentTab);
  const mobileCurrentTab = useSelector(
    state => state.global.currentMobileTab[itemId],
  );
  const listCampaignsId = useSelector(state => state.global.listCampaignsId);
  const tabIds = useMemo(() => {
    if (!item?.tabs) {
      return [];
    }
    return item.tabs.map(tabObj => getId(tabObj)).filter(Boolean);
  }, [item?.tabs]);
  const firstAccessibleTabId = useMemo(() => {
    for (const tabId of tabIds) {
      if (isBuiltinTabId(tabId)) {
        return tabId;
      }
      const tab = items?.[tabId];
      if (!tab) {
        continue;
      }
      if (isTabCampaignAccessible(tab, items, listCampaignsId)) {
        return tabId;
      }
    }
    return null;
  }, [tabIds, items, listCampaignsId]);
  const currentTabCandidate = useMobileTabState ? mobileCurrentTab : currentTab;
  const currentTabId = useMemo(() => {
    if (!currentTabCandidate) {
      return firstAccessibleTabId;
    }
    if (isBuiltinTabId(currentTabCandidate)) {
      return currentTabCandidate;
    }
    const tab = items?.[currentTabCandidate];
    if (!tab) {
      return firstAccessibleTabId;
    }
    if (isTabCampaignAccessible(tab, items, listCampaignsId)) {
      return currentTabCandidate;
    }
    return firstAccessibleTabId;
  }, [
    currentTabCandidate,
    firstAccessibleTabId,
    items,
    listCampaignsId,
  ]);
  const currentBoardTab = useSelector(state =>
    currentTabId ? state.items[currentTabId] : undefined,
  );
  const dispatch = useDispatch();
  const windowSize = useWindowSize();
  const effectiveNavigation = hideNavigation
    ? { open: false, size: 0 }
    : navigation;
  const baseWidth =
    typeof containerWidth === 'number' ? containerWidth : windowSize.width;
  const sidebarOffset = ignoreSidebar ? 0 : sidebar.open ? sidebar.size : 0;
  const navigationOffset = effectiveNavigation.open
    ? effectiveNavigation.size
    : 0;
  const availableWidth = baseWidth - sidebarOffset - navigationOffset;
  const rightInset = effectiveNavigation.open
    ? `calc(${effectiveNavigation.size}px + 1em)`
    : '1em';
  const toolBoxPositionClass = 'fixed';
  const toolBoxRightInset = navigation.open
    ? `calc(${navigation.size}px + 1em)`
    : '1em';
  const effectiveSidebarOpen =
    typeof sidebarOpenOverride === 'boolean' ? sidebarOpenOverride : sidebar.open;
  const toolBoxLeftInset =
    ignoreSidebar || !effectiveSidebarOpen
      ? '1em'
      : `calc(${sidebar.size}px + 1em)`;

  const [tabDisplay, setTabDisplay] = useState('grid');
  const [masonry, setMasonry] = useState(false);
  const [tabOverlayMounted, setTabOverlayMounted] = useState(false);
  const [tabOverlayVisible, setTabOverlayVisible] = useState(false);
  const canMj = isMjForItem(item, items) || canUseMjForItem(item, items);
  const canEdit = canEditItemForItem(item, items);
  const boardReadOnlyMode = !canMj ? currentBoardTab?.readOnlyMode || null : null;
  const isBoardHardReadOnly = boardReadOnlyMode === 'hard';
  const canEditBoardWidgets = isEditable() && canEdit && !isBoardHardReadOnly;
  const canEditBoardLayout = isEditable() && canEdit && !boardReadOnlyMode;
  const isBoardToolboxReadOnly = !canEditBoardWidgets;
  const isValidObjectId = id => /^[a-f0-9]{24}$/i.test(id);
  const inflightFetchesRef = React.useRef(new Set());
  const fetchedOnceRef = React.useRef(new Set());
  const {
    mapZoom,
    setMapZoom,
    mapTool,
    setMapTool,
    mapMeasureMode,
    setMapMeasureMode,
    mapMeasureScales,
    setMapMeasureScales,
    mapActiveMeasureScaleIds,
    setMapActiveMeasureScaleIds,
    mapToggleMeasureScaleCommand,
    mapToggleMeasureScaleCommandTick,
    mapShapeType,
    mapInteractionMode,
    mapZoomCommand,
    mapZoomCommandTick,
    mapCanShowColorPalette,
    setMapCanShowColorPalette,
    mapCanShowLineOptions,
    setMapCanShowLineOptions,
    mapSelectedPinColor,
    setMapSelectedPinColor,
    mapPinColorCommand,
    mapPinColorCommandTick,
    mapSelectedPinSize,
    setMapSelectedPinSize,
    mapPinSizeCommand,
    mapPinSizeCommandTick,
    mapLinkActionMode,
    setMapLinkActionMode,
    mapSelectedPinIconSlug,
    setMapSelectedPinIconSlug,
    mapPinIconCommand,
    mapPinIconCommandTick,
    mapUnlinkItemCommandTick,
    mapOpenLinkModalCommandTick,
    mapOpenBackgroundModalCommandTick,
    mapOpenGridSettingsModalCommandTick,
    mapToggleGridVisibleCommandTick,
    mapCanShowRegionStyleOptions,
    setMapCanShowRegionStyleOptions,
    mapSelectedRegionColor,
    setMapSelectedRegionColor,
    mapSelectedRegionBorderStyle,
    setMapSelectedRegionBorderStyle,
    mapRegionColorCommand,
    mapRegionColorCommandTick,
    mapRegionBorderStyleCommand,
    mapRegionBorderStyleCommandTick,
    mapCanShowShapeColorOptions,
    setMapCanShowShapeColorOptions,
    mapCanShowShapeLayerOptions,
    setMapCanShowShapeLayerOptions,
    mapSelectedShapeType,
    setMapSelectedShapeType,
    mapSelectedShapeColor,
    setMapSelectedShapeColor,
    mapSelectedShapeBorderStyle,
    setMapSelectedShapeBorderStyle,
    mapShapeTypeCommand,
    mapShapeTypeCommandTick,
    mapShapeColorCommand,
    mapShapeColorCommandTick,
    mapShapeLayerCommand,
    mapShapeLayerCommandTick,
    mapCanShowDrawingColorOptions,
    setMapCanShowDrawingColorOptions,
    mapSelectedDrawingColor,
    setMapSelectedDrawingColor,
    mapSelectedDrawingBorderStyle,
    setMapSelectedDrawingBorderStyle,
    mapDrawingColorCommand,
    mapDrawingColorCommandTick,
    mapDrawingBorderStyleCommand,
    mapDrawingBorderStyleCommandTick,
    mapSelectedLineColor,
    setMapSelectedLineColor,
    mapSelectedLineArrows,
    setMapSelectedLineArrows,
    mapSelectedLineBorderStyle,
    setMapSelectedLineBorderStyle,
    mapLineColorCommand,
    mapLineColorCommandTick,
    mapLineArrowsCommand,
    mapLineArrowsCommandTick,
    mapCanCreateGroup,
    setMapCanCreateGroup,
    mapCanEnterGroup,
    setMapCanEnterGroup,
    mapCanUngroup,
    setMapCanUngroup,
    mapIsInsideGroup,
    setMapIsInsideGroup,
    setMapActiveGroupId,
    mapActiveGroupLabel,
    setMapActiveGroupLabel,
    mapGroupCommand,
    mapGroupCommandTick,
    dispatchMapZoomCommand,
    dispatchMapPinSize,
    dispatchMapPinIcon,
    dispatchMapUnlinkItemCommand,
    dispatchMapOpenLinkModalCommand,
    dispatchMapOpenBackgroundModalCommand,
    dispatchMapOpenGridSettingsModalCommand,
    dispatchMapToggleGridVisibleCommand,
    dispatchMapShapeType,
    dispatchMapShapeLayer,
    dispatchMapSelectionColor,
    dispatchMapSelectionBorderStyle,
    dispatchMapLineArrows,
    dispatchMapGroupCommand,
    dispatchMapToggleMeasureScale,
    handleMapInteractionModeChange,
  } = useBoardMapController();

  const getItems = async id => {
    if (!id || !isValidObjectId(id)) {
      return;
    }
    if (inflightFetchesRef.current.has(id)) {
      return;
    }
    if (fetchedOnceRef.current.has(id)) {
      const existing = items?.[id];
      if (existing && existing.__partial !== true) {
        return;
      }
    }
    inflightFetchesRef.current.add(id);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/items/${id}`);
      if (res.data) {
        dispatch(saveAll({ item: res.data }));
      }
      fetchedOnceRef.current.add(id);
    } catch (error) {
      // Ignore transient 404s when an item was just deleted.
      if (error?.response?.status !== 404) {
        console.error('Failed to fetch item:', id, error);
      }
    } finally {
      inflightFetchesRef.current.delete(id);
    }
  };

  useEffect(() => {
    if (disableSelfFetch) {
      return;
    }
    if (!itemId) {
      return;
    }
    if (!item || item.__partial) {
      getItems(itemId);
    }
  }, [itemId, item, disableSelfFetch]);

  useEffect(() => {
    if (disableSelfFetch) {
      return;
    }
    if (!currentTabId) {
      return;
    }
    if (
      (!currentBoardTab || currentBoardTab.__partial) &&
      isValidObjectId(currentTabId)
    ) {
      getItems(currentTabId);
    }
  }, [currentTabId, currentBoardTab, disableSelfFetch]);

  useEffect(() => {
    if (item?.tabs && currentTabId) {
      const tabConfig = item.tabs.find(
        tabObj => getId(tabObj) === currentTabId,
      );
      if (tabConfig) {
        setTabDisplay(tabConfig[currentTabId]?.display);
      }
    }
  }, [item?.tabs, currentTabId]);

  useEffect(() => {
    if (!useMobileTabState || tabIds.length === 0) {
      return;
    }
    if (
      firstAccessibleTabId &&
      (!mobileCurrentTab || mobileCurrentTab !== currentTabId)
    ) {
      dispatch(setCurrentMobileTab({ itemId, tabId: firstAccessibleTabId }));
    }
  }, [
    useMobileTabState,
    tabIds,
    firstAccessibleTabId,
    mobileCurrentTab,
    currentTabId,
    itemId,
    dispatch,
  ]);

  useLayoutEffect(() => {
    setTabOverlayMounted(true);
    setTabOverlayVisible(true);
    const startFadeTimeoutId = window.setTimeout(() => {
      setTabOverlayVisible(false);
    }, 50);
    const timeoutId = window.setTimeout(() => {
      setTabOverlayMounted(false);
    }, 400);
    return () => {
      window.clearTimeout(startFadeTimeoutId);
      window.clearTimeout(timeoutId);
    };
  }, [currentTabId, tabDisplay]);

  const hideBoardWidgetsDuringOverlay = tabOverlayVisible === true;
  const effectiveTabDisplay = tabDisplay;

  return (
    <div className='flex-1 flex flex-col bg-primary-d bg-opacity-75 relative overflow-x-hidden'>
      {isEditable() && (
        <TabAll
          itemId={itemId}
          containerWidth={baseWidth}
          ignoreSidebar={ignoreSidebar}
          useMobileTabState={useMobileTabState}
          hideNavigation={hideNavigation}
          sidebarOpenOverride={sidebarOpenOverride}
          onSidebarOpen={onSidebarOpen}
        />
      )}
      <div
        className='flex'
        style={{
          height: `${
              isReadOnly()
              ? '100svh'
              : `calc(100svh - 41px ${
                  isEditable() === false ? '+ 30px' : ''
                })`
          } `,
        }}
      >
        <div
          className={isReadOnly() ? '' : 'pb-4'}
        >
          <div
            className='h-full relative overflow-y-auto overflow-x-hidden flex-1 transition-all'
            style={{
              width: availableWidth,
            }}
          >
            {tabOverlayMounted && (
              <div
                className={`absolute inset-0 z-20 pointer-events-none bg-main transition-opacity duration-300 ${
                  tabOverlayVisible ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <div className='bg-primary-d bg-opacity-75 h-full' />
              </div>
            )}
            {currentTabId === 'link' && <LinkAll itemId={itemId} />}
            {currentTabId === 'link' && isEditable() && canEdit && (
              <div
                className='fixed bottom-1 transition-all'
                style={{
                  right: rightInset,
                }}
              >
                <BtnRound
                  onClick={() =>
                    dispatch(
                      setOpenModales({ id: 'addLink' + itemId, open: true }),
                    )
                  }
                >
                  <FaPlus />
                </BtnRound>
              </div>
            )}
            {currentTabId === 'text' && <TextAll itemId={itemId} />}
            {currentTabId === 'text' && isEditable() && canEdit && (
              <div
                className='fixed bottom-1 transition-all'
                style={{
                  right: rightInset,
                }}
              >
                <BtnRound onClick={() => addText(item)}>
                  <FaPlus />
                </BtnRound>
              </div>
            )}
            {currentTabId === 'img' && (
              <PicAll
                itemId={itemId}
                masonry={masonry}
              />
            )}
            {currentTabId === 'img' && (
              <div
                className='fixed bottom-1 transition-all flex items-center gap-1'
                style={{
                  right: rightInset,
                }}
              >
                {!masonry && (
                  <BtnRound onClick={() => dispatch(setPicZoom('more'))}>
                    <FaSearchPlus />
                  </BtnRound>
                )}
                {!masonry && (
                  <BtnRound onClick={() => dispatch(setPicZoom('less'))}>
                    <FaSearchMinus />
                  </BtnRound>
                )}

                {!masonry && (
                  <BtnRound onClick={() => setMasonry(true)}>
                    <RiLayoutMasonryFill />
                  </BtnRound>
                )}

                {masonry && (
                  <BtnRound onClick={() => setMasonry(false)}>
                    <RiLayoutGridFill />
                  </BtnRound>
                )}
                {isEditable() && canEdit && (
                  <BtnRound
                    onClick={() =>
                      dispatch(setOpenModales({ id: 'addPic', open: true }))
                    }
                  >
                    <FaPlus />
                  </BtnRound>
                )}
              </div>
            )}
            {currentTabId === 'num' && <NumAll itemId={itemId} />}
            {currentTabId === 'num' && isEditable() && canEdit && (
              <div
                className='fixed bottom-1 transition-all'
                style={{
                  right: rightInset,
                }}
              >
                <BtnRound onClick={() => addNum(item)}>
                  <FaPlus />
                </BtnRound>
              </div>
            )}
            {currentTabId === 'conditions' && <ConditionAll itemId={itemId} />}
            {currentTabId === 'conditions' && isEditable() && canEdit && (
              <div
                className='fixed bottom-1 transition-all'
                style={{
                  right: rightInset,
                }}
              >
                <BtnRound onClick={() => addCondition(item, null, () => {})}>
                  <FaPlus />
                </BtnRound>
              </div>
            )}
            {(effectiveTabDisplay === 'grid' || !effectiveTabDisplay) &&
              currentTabId !== 'img' &&
              currentTabId !== 'text' &&
              currentTabId !== 'link' &&
              currentTabId !== 'num' &&
              currentTabId !== 'conditions' &&
              !hideBoardWidgetsDuringOverlay && (
                <BoardGrid
                  itemId={itemId}
                  boardId={currentTabId}
                  boardWidth={availableWidth}
                  editable={canEditBoardWidgets}
                  layoutEditable={canEditBoardLayout}
                />
              )}
            {effectiveTabDisplay === 'map' &&
              currentTabId !== 'img' &&
              currentTabId !== 'text' &&
              currentTabId !== 'link' &&
              currentTabId !== 'num' &&
              currentTabId !== 'conditions' &&
              !hideBoardWidgetsDuringOverlay && (
                <BoardMap
                  itemId={itemId}
                  boardId={currentTabId}
                  boardWidth={availableWidth}
                  editable={canEditBoardWidgets}
                  layoutEditable={canEditBoardLayout}
                  onZoomChange={setMapZoom}
                  interactionMode={mapInteractionMode}
                  mapTool={mapTool}
                  measureMode={mapMeasureMode}
                  mapShapeType={mapShapeType}
                  onMapToolChange={setMapTool}
                  onMeasureModeChange={setMapMeasureMode}
                  onMeasureScalesChange={setMapMeasureScales}
                  onActiveMeasureScaleIdsChange={setMapActiveMeasureScaleIds}
                  onPinSelectionChange={setMapCanShowColorPalette}
                  onLineSelectionChange={setMapCanShowLineOptions}
                  onSelectedLineColorChange={setMapSelectedLineColor}
                  onSelectedLineArrowsChange={setMapSelectedLineArrows}
                  onSelectedLineBorderStyleChange={setMapSelectedLineBorderStyle}
                  onSelectedPinColorChange={setMapSelectedPinColor}
                  onSelectedPinSizeChange={setMapSelectedPinSize}
                  onSelectedPinIconChange={setMapSelectedPinIconSlug}
                  onShapeSelectionChange={setMapCanShowShapeColorOptions}
                  onShapeLayerSelectionChange={setMapCanShowShapeLayerOptions}
                  onSelectedShapeTypeChange={setMapSelectedShapeType}
                  onSelectedShapeColorChange={setMapSelectedShapeColor}
                  onSelectedShapeBorderStyleChange={
                    setMapSelectedShapeBorderStyle
                  }
                  onRegionSelectionChange={setMapCanShowRegionStyleOptions}
                  onSelectedRegionColorChange={setMapSelectedRegionColor}
                  onSelectedRegionBorderStyleChange={setMapSelectedRegionBorderStyle}
                  onLinkActionChange={setMapLinkActionMode}
                  onDrawingSelectionChange={setMapCanShowDrawingColorOptions}
                  onSelectedDrawingColorChange={setMapSelectedDrawingColor}
                  onSelectedDrawingBorderStyleChange={setMapSelectedDrawingBorderStyle}
                  onCreateGroupAvailabilityChange={setMapCanCreateGroup}
                  onEnterGroupAvailabilityChange={setMapCanEnterGroup}
                  onUngroupAvailabilityChange={setMapCanUngroup}
                  onInsideGroupChange={setMapIsInsideGroup}
                  onActiveGroupIdChange={setMapActiveGroupId}
                  onActiveGroupLabelChange={setMapActiveGroupLabel}
                  pinColorCommand={mapPinColorCommand}
                  pinColorCommandTick={mapPinColorCommandTick}
                  pinSizeCommand={mapPinSizeCommand}
                  pinSizeCommandTick={mapPinSizeCommandTick}
                  pinIconCommand={mapPinIconCommand}
                  pinIconCommandTick={mapPinIconCommandTick}
                  unlinkItemCommandTick={mapUnlinkItemCommandTick}
                  openLinkModalCommandTick={mapOpenLinkModalCommandTick}
                  openBackgroundModalCommandTick={mapOpenBackgroundModalCommandTick}
                  openGridSettingsModalCommandTick={mapOpenGridSettingsModalCommandTick}
                  toggleGridVisibleCommandTick={mapToggleGridVisibleCommandTick}
                  shapeTypeCommand={mapShapeTypeCommand}
                  shapeTypeCommandTick={mapShapeTypeCommandTick}
                  shapeColorCommand={mapShapeColorCommand}
                  shapeColorCommandTick={mapShapeColorCommandTick}
                  shapeLayerCommand={mapShapeLayerCommand}
                  shapeLayerCommandTick={mapShapeLayerCommandTick}
                  regionColorCommand={mapRegionColorCommand}
                  regionColorCommandTick={mapRegionColorCommandTick}
                  regionBorderStyleCommand={mapRegionBorderStyleCommand}
                  regionBorderStyleCommandTick={mapRegionBorderStyleCommandTick}
                  drawingColorCommand={mapDrawingColorCommand}
                  drawingColorCommandTick={mapDrawingColorCommandTick}
                  drawingBorderStyleCommand={mapDrawingBorderStyleCommand}
                  drawingBorderStyleCommandTick={mapDrawingBorderStyleCommandTick}
                  lineColorCommand={mapLineColorCommand}
                  lineColorCommandTick={mapLineColorCommandTick}
                  lineArrowsCommand={mapLineArrowsCommand}
                  lineArrowsCommandTick={mapLineArrowsCommandTick}
                  groupCommand={mapGroupCommand}
                  groupCommandTick={mapGroupCommandTick}
                  zoomCommand={mapZoomCommand}
                  zoomCommandTick={mapZoomCommandTick}
                  toggleMeasureScaleCommand={mapToggleMeasureScaleCommand}
                  toggleMeasureScaleCommandTick={mapToggleMeasureScaleCommandTick}
                />
              )}
            {(effectiveTabDisplay === 'grid' ||
              effectiveTabDisplay === 'map' ||
              !effectiveTabDisplay) &&
              currentTabId !== 'img' &&
              currentTabId !== 'text' &&
              currentTabId !== 'link' &&
              currentTabId !== 'num' &&
              currentTabId !== 'conditions' &&
              isEditable() &&
              canEdit &&
              !boardReadOnlyMode &&
              !hideBoardWidgetsDuringOverlay && (
                <div
                  className={`bg-main ${toolBoxPositionClass} shadow rounded bottom-1 right-1 px-1 py-05 transition-all`}
                  style={{
                    right: toolBoxRightInset,
                  }}
                >
                  <ItemToolBox
                    itemId={itemId}
                    displayType={'large'}
                    currentBoard={currentTabId}
                    hasBoard={true}
                  />
                </div>
              )}
            {effectiveTabDisplay === 'map' &&
              currentTabId !== 'img' &&
              currentTabId !== 'text' &&
              currentTabId !== 'link' &&
              currentTabId !== 'num' &&
              currentTabId !== 'conditions' &&
              !hideBoardWidgetsDuringOverlay && (
                <BoardToolBoxMap
                  zoom={mapZoom}
                  leftInset={toolBoxLeftInset}
                  rightInset={toolBoxRightInset}
                  showOnlyZoom={isBoardToolboxReadOnly}
                  showMeasureInReadOnly={isBoardToolboxReadOnly}
                  allowMeasureSettings={!isBoardToolboxReadOnly}
                  mapTool={mapTool}
                  mapShapeType={
                    mapCanShowShapeColorOptions ? mapSelectedShapeType : mapShapeType
                  }
                  interactionMode={mapInteractionMode}
                  measureMode={mapMeasureMode}
                  showColorPalette={
                    mapCanShowLineOptions ||
                    mapCanShowColorPalette ||
                    mapCanShowShapeColorOptions ||
                    mapCanShowRegionStyleOptions ||
                    mapCanShowDrawingColorOptions
                  }
                  selectedColor={
                    mapCanShowLineOptions
                      ? mapSelectedLineColor
                      : mapCanShowColorPalette
                      ? mapSelectedPinColor
                      : mapCanShowShapeColorOptions
                        ? mapSelectedShapeColor
                        : mapCanShowRegionStyleOptions
                          ? mapSelectedRegionColor
                          : mapSelectedDrawingColor
                  }
                  selectedPinSize={mapSelectedPinSize}
                  showShapeColorOptions={mapCanShowShapeColorOptions}
                  showShapeLayerOptions={mapCanShowShapeLayerOptions}
                  showBorderStyleOptions={
                    mapCanShowLineOptions ||
                    mapCanShowShapeColorOptions ||
                    mapCanShowRegionStyleOptions ||
                    mapCanShowDrawingColorOptions
                  }
                  showArrowOptions={mapCanShowLineOptions}
                  linkArrows={mapSelectedLineArrows}
                  selectedBorderStyle={
                    mapCanShowLineOptions
                      ? mapSelectedLineBorderStyle
                      : mapCanShowShapeColorOptions
                        ? mapSelectedShapeBorderStyle
                        : mapCanShowRegionStyleOptions
                      ? mapSelectedRegionBorderStyle
                      : mapSelectedDrawingBorderStyle
                  }
                  showLinkItem={
                    mapLinkActionMode === 'link'
                  }
                  showUnlinkItem={mapLinkActionMode === 'unlink'}
                  showIconPicker={mapCanShowColorPalette}
                  selectedPinIconSlug={mapSelectedPinIconSlug}
                  onInteractionModeChange={handleMapInteractionModeChange}
                  onMapToolChange={setMapTool}
                  onMeasureModeChange={setMapMeasureMode}
                  measureScales={mapMeasureScales}
                  activeMeasureScaleIds={mapActiveMeasureScaleIds}
                  mapGridVisible={currentBoardTab?.map?.grid?.visible === true}
                  onToggleMeasureScale={dispatchMapToggleMeasureScale}
                  onMapShapeTypeChange={dispatchMapShapeType}
                  onLayerChange={dispatchMapShapeLayer}
                  onOpenBackgroundModal={dispatchMapOpenBackgroundModalCommand}
                  onOpenGridSettingsModal={dispatchMapOpenGridSettingsModalCommand}
                  onToggleGridVisible={dispatchMapToggleGridVisibleCommand}
                  onColorChange={dispatchMapSelectionColor}
                  onLinkArrowsChange={dispatchMapLineArrows}
                  onPinSizeChange={dispatchMapPinSize}
                  onPinIconChange={dispatchMapPinIcon}
                  onUnlinkItem={dispatchMapUnlinkItemCommand}
                  onOpenLinkModal={dispatchMapOpenLinkModalCommand}
                  onBorderStyleChange={dispatchMapSelectionBorderStyle}
                  onZoomOut={() => dispatchMapZoomCommand('out')}
                  onZoomReset={() => dispatchMapZoomCommand('reset')}
                  onZoomIn={() => dispatchMapZoomCommand('in')}
                  showCreateGroup={mapCanCreateGroup}
                  onCreateGroup={() => dispatchMapGroupCommand('create')}
                  showEnterGroup={mapCanEnterGroup}
                  onEnterGroup={() => dispatchMapGroupCommand('enter')}
                  showUngroup={mapCanUngroup}
                  onUngroup={() => dispatchMapGroupCommand('ungroup')}
                  showExitGroup={mapIsInsideGroup}
                  onExitGroup={() => dispatchMapGroupCommand('exit')}
                  activeGroupLabel={mapActiveGroupLabel}
                />
              )}
          </div>
        </div>

        {!hideNavigation && effectiveNavigation.open && (
          <NavigationGlobal itemId={itemId} />
        )}
      </div>
    </div>
  );
};
